/**
 * Build platform slot payloads for POST /consultations/clinics/:id/slots { slots: [...] }.
 * Times are interpreted in the browser's local timezone (same as datetime-local inputs).
 */

const MAX_SLOTS = 200;

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * @param {string} dateFromYmd - YYYY-MM-DD
 * @param {string} dateToYmd - YYYY-MM-DD
 * @param {boolean[]} weekdaysSunFirst - length 7, index 0 = Sunday … 6 = Saturday
 * @param {string} timeStart - HH:mm
 * @param {string} timeEnd - HH:mm
 * @param {number} durationMinutes
 * @param {string} [practitionerId]
 * @returns {{ slots: Array<{startAt:string,endAt:string,practitioner?:string}>, skippedPast: number, error?: string }}
 */
export function buildRecurringSlotPayloads(
  dateFromYmd,
  dateToYmd,
  weekdaysSunFirst,
  timeStart,
  timeEnd,
  durationMinutes,
  practitionerId
) {
  if (!dateFromYmd || !dateToYmd) {
    return { slots: [], skippedPast: 0, error: "Date range is required." };
  }
  const dur = Number(durationMinutes);
  if (!Number.isFinite(dur) || dur < 10 || dur > 240) {
    return { slots: [], skippedPast: 0, error: "Slot duration must be between 10 and 240 minutes." };
  }
  if (!Array.isArray(weekdaysSunFirst) || weekdaysSunFirst.length !== 7) {
    return { slots: [], skippedPast: 0, error: "Weekday selection is invalid." };
  }
  if (!weekdaysSunFirst.some(Boolean)) {
    return { slots: [], skippedPast: 0, error: "Select at least one weekday." };
  }

  const startDay = new Date(`${dateFromYmd}T12:00:00`);
  const endDay = new Date(`${dateToYmd}T12:00:00`);
  if (Number.isNaN(startDay.getTime()) || Number.isNaN(endDay.getTime())) {
    return { slots: [], skippedPast: 0, error: "Invalid dates." };
  }
  if (endDay < startDay) {
    return { slots: [], skippedPast: 0, error: "End date must be on or after start date." };
  }

  const [sh, sm] = timeStart.split(":").map((x) => parseInt(x, 10));
  const [eh, em] = timeEnd.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(sh) || Number.isNaN(sm) || Number.isNaN(eh) || Number.isNaN(em)) {
    return { slots: [], skippedPast: 0, error: "Start and end times must be HH:mm." };
  }

  const slots = [];
  let skippedPast = 0;
  const now = Date.now();

  for (let cursor = new Date(startDay); cursor <= endDay; cursor.setDate(cursor.getDate() + 1)) {
    const dow = cursor.getDay();
    if (!weekdaysSunFirst[dow]) continue;

    const ymd = toYmd(cursor);
    let segmentStart = new Date(`${ymd}T${pad2(sh)}:${pad2(sm)}:00`);
    const dayEnd = new Date(`${ymd}T${pad2(eh)}:${pad2(em)}:00`);

    if (Number.isNaN(segmentStart.getTime()) || Number.isNaN(dayEnd.getTime())) {
      return { slots: [], skippedPast: 0, error: "Could not build times for one of the days." };
    }
    if (dayEnd <= segmentStart) {
      return { slots: [], skippedPast: 0, error: "End time must be after start time on the same day." };
    }

    while (segmentStart < dayEnd) {
      const segmentEnd = new Date(segmentStart.getTime() + dur * 60 * 1000);
      if (segmentEnd > dayEnd) break;

      if (segmentStart.getTime() <= now) {
        skippedPast += 1;
      } else {
        const row = {
          startAt: segmentStart.toISOString(),
          endAt: segmentEnd.toISOString(),
        };
        if (practitionerId) row.practitioner = practitionerId;
        slots.push(row);
        if (slots.length >= MAX_SLOTS) {
          return {
            slots,
            skippedPast,
            error: `Stopped at ${MAX_SLOTS} slots (safety limit). Narrow the range or increase slot length.`,
          };
        }
      }
      segmentStart = segmentEnd;
    }
  }

  return { slots, skippedPast };
}
